import { FIELD_LABELS, RESOURCE_LABELS, UNIT_LABELS, type FieldType } from '@bienenkampf/shared';
import { flightTimeMinutes, hexDistance } from '@bienenkampf/sim';
import { Badge, Button, Card, Input } from '@bienenkampf/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  HexMap,
  type HexMapApi,
  type MapHighlight,
  type MapMovement,
  type MapViewport,
} from '../components/HexMap.js';
import { MiniMap } from '../components/MiniMap.js';
import { HivePanel } from '../components/HivePanel.js';
import { useNow } from '../hooks/useNow.js';
import { FIELD_ICONS } from '../lib/hex.js';
import { formatAbsRel, formatDuration, formatNumber } from '../lib/time.js';
import { pickActive, useActiveHiveId } from '../lib/activeHive.js';
import { useTRPC } from '../lib/trpc.js';

type Hex = { q: number; r: number };

export function MapPage() {
  const { worldId: worldIdStr } = useParams({ from: '/w/$worldId' });
  const worldId = Number(worldIdStr);
  const trpc = useTRPC();
  const mine = useQuery(trpc.hive.mine.queryOptions({ worldId }));
  const activeId = useActiveHiveId(worldId);
  const myHive = pickActive(mine.data?.hives, activeId);
  const [center, setCenter] = useState<Hex | null>(null);
  const [selected, setSelected] = useState<Hex | null>(null);
  const [highlight, setHighlight] = useState<MapHighlight>(null);
  const [viewport, setViewport] = useState<MapViewport | null>(null);
  const mapApi = useRef<HexMapApi | null>(null);
  const cfg = useQuery(trpc.world.config.queryOptions({ worldId }));
  const overview = useQuery({
    ...trpc.map.overview.queryOptions({ worldId }),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
  const incoming = useQuery({
    ...trpc.movement.incoming.queryOptions({ worldId }),
    refetchInterval: 30_000,
  });
  // Tastatur (Desktop): Pfeile schwenken, +/- zoomen, Pos1 zum eigenen Stock
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT'))
        return;
      const step = 80;
      if (e.key === 'ArrowLeft') mapApi.current?.panBy(-step, 0);
      else if (e.key === 'ArrowRight') mapApi.current?.panBy(step, 0);
      else if (e.key === 'ArrowUp') mapApi.current?.panBy(0, -step);
      else if (e.key === 'ArrowDown') mapApi.current?.panBy(0, step);
      else if (e.key === '+' || e.key === '=') mapApi.current?.zoomBy(1.25);
      else if (e.key === '-') mapApi.current?.zoomBy(1 / 1.25);
      else if (e.key === 'Home' && myHive) mapApi.current?.centerOn(myHive.q, myHive.r);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [myHive]);
  const search = useSearch({ from: '/w/$worldId/' });
  useEffect(() => {
    if (search.q !== undefined && search.r !== undefined) {
      const hex = { q: search.q, r: search.r };
      setCenter(hex);
      setSelected(hex);
      mapApi.current?.centerOn(hex.q, hex.r);
    }
  }, [search.q, search.r]);
  useEffect(() => {
    if (myHive && !center && search.q === undefined) setCenter({ q: myHive.q, r: myHive.r });
  }, [myHive, center, search.q]);

  const view = useQuery({
    ...trpc.map.view.queryOptions({ worldId, q: center?.q ?? 0, r: center?.r ?? 0, radius: 26 }),
    enabled: center !== null,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const moves = useQuery({
    ...trpc.movement.list.queryOptions({ worldId }),
    refetchInterval: 30_000,
  });

  const movements: MapMovement[] = useMemo(
    () => [
      ...(incoming.data ?? []).map((m) => ({
        id: -m.id,
        status: 'FLYING',
        from: { q: m.fromQ, r: m.fromR },
        to: { q: m.targetQ, r: m.targetR },
        departedAt: m.departedAt,
        arrivesAt: m.arrivesAt,
        leavesAt: null,
        returnsAt: null,
        incoming: m.type !== 'QUEEN_DELIVERY' && m.type !== 'SUPPORT',
      })),
      ...(moves.data?.active ?? [])
        .filter((m) => m.toField || m.toHive || m.toHex)
        .map((m) => ({
          id: m.id,
          status: m.status,
          from: { q: m.fromHive.q, r: m.fromHive.r },
          to: m.toField
            ? { q: m.toField.q, r: m.toField.r }
            : m.toHive
              ? { q: m.toHive.q, r: m.toHive.r }
              : { q: m.toHex!.q, r: m.toHex!.r },
          departedAt: m.departedAt,
          arrivesAt: m.arrivesAt,
          leavesAt: m.leavesAt,
          returnsAt: m.returnsAt,
        })),
    ],
    [moves.data, incoming.data],
  );

  if (!center) return <p className="text-stone-400">Lade Karte…</p>;
  const selField = selected
    ? view.data?.fields.find((f) => f.q === selected.q && f.r === selected.r)
    : undefined;
  const selHive = selected
    ? view.data?.hives.find((h) => h.q === selected.q && h.r === selected.r)
    : undefined;

  return (
    <div className="flex flex-col gap-3 lg:grid lg:grid-cols-[1fr_22rem] lg:items-start">
      <div className="relative h-[62dvh] overflow-hidden rounded-2xl border border-stone-800 shadow-[inset_0_0_60px_rgba(0,0,0,0.55)] lg:h-[calc(100dvh-9.5rem)]">
        <HexMap
          hives={(view.data?.hives ?? []).map((h) => ({
            ...h,
            protectionUntil: h.protectionUntil,
          }))}
          fields={view.data?.fields ?? []}
          movements={movements}
          center={center}
          selected={selected}
          highlight={highlight}
          onSelect={setSelected}
          onCenterChange={setCenter}
          onViewport={setViewport}
          onReady={(api) => (mapApi.current = api)}
        />
        {/* Vignette */}
        <div className="pointer-events-none absolute inset-0 rounded-2xl shadow-[inset_0_0_80px_rgba(0,0,0,0.6)]" />
        <div className="pointer-events-none absolute left-2 top-2 hidden text-[11px] sm:block">
          <Badge className="bg-stone-950/70">
            Ziehen: bewegen · Rad/Pinch: Zoom · Tippen: auswählen · Pfeile/+/−/Pos1
          </Badge>
        </div>
        <div className="absolute left-2 top-2 flex flex-wrap gap-1 text-[11px] sm:top-8">
          {(
            [
              ['free', '🏚 frei'],
              ['queenless', '👑 weisellos'],
              ['hostile', '⚔ Feinde'],
              ['nectar', '🍯 Nektar'],
              ['pollen', '🌸 Pollen'],
            ] as Array<[MapHighlight, string]>
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setHighlight((h) => (h === key ? null : key))}
              className={`rounded-full border px-2 py-0.5 backdrop-blur ${highlight === key ? 'border-amber-400 bg-amber-500/30 text-amber-100' : 'border-stone-700 bg-stone-950/70 text-stone-300'}`}
            >
              {label}
            </button>
          ))}
        </div>
        {overview.data && (
          <div className="absolute bottom-2 right-2 hidden sm:block">
            <MiniMap
              hives={overview.data.hives}
              fields={overview.data.fields}
              radius={cfg.data?.balance.map.radius ?? 50}
              viewport={viewport}
              onJump={(q, r) => {
                mapApi.current?.centerOn(q, r);
                setSelected({ q, r });
              }}
            />
          </div>
        )}
        {selected && (
          <div className="pointer-events-none absolute left-2 bottom-2 text-[11px]">
            <Badge className="bg-stone-950/70 tabular-nums">
              [{selected.q}|{selected.r}]
            </Badge>
          </div>
        )}
        <div className="absolute right-2 top-2 flex flex-col gap-1">
          <MapButton onClick={() => mapApi.current?.zoomBy(1.25)} title="Hineinzoomen">
            +
          </MapButton>
          <MapButton onClick={() => mapApi.current?.zoomBy(1 / 1.25)} title="Herauszoomen">
            −
          </MapButton>
          {myHive && (
            <MapButton
              onClick={() => {
                mapApi.current?.centerOn(myHive.q, myHive.r);
                setSelected({ q: myHive.q, r: myHive.r });
              }}
              title="Zu meinem Stock"
            >
              ⌂
            </MapButton>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-3">
        {selField && myHive && (
          <FieldPanel worldId={worldId} fieldId={selField.id} field={selField} myHive={myHive} />
        )}
        {selHive && <HivePanel worldId={worldId} hive={selHive} myHive={myHive} />}
        {!selField && !selHive && (
          <Card title="Blütenfelder">
            <p className="text-sm text-stone-400">
              Tippe ein Feld an, um Sammlerinnen hinzuschicken. Helle Punkte am oberen Rand zeigen,
              wie viele Parteien gerade dort sammeln.
            </p>
            <ul className="mt-3 grid grid-cols-2 gap-1 text-xs text-stone-300">
              {(Object.keys(FIELD_LABELS) as FieldType[]).map((t) => (
                <li key={t}>
                  {FIELD_ICONS[t]} {FIELD_LABELS[t]}
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}

function MapButton(props: { onClick: () => void; title: string; children: string }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      title={props.title}
      className="grid size-10 place-items-center rounded-lg border border-stone-700 bg-stone-950/80 text-lg text-stone-100 shadow backdrop-blur hover:bg-stone-800"
    >
      {props.children}
    </button>
  );
}

function FieldPanel({
  worldId,
  fieldId,
  field,
  myHive,
}: {
  worldId: number;
  fieldId: number;
  field: {
    q: number;
    r: number;
    type: FieldType;
    resource: 'nectar' | 'pollen';
    baseRate: number;
    parties: number;
  };
  myHive: { id: number; q: number; r: number };
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const now = useNow();
  const detail = useQuery({ ...trpc.map.field.queryOptions({ fieldId }), refetchInterval: 30_000 });
  const hive = useQuery(trpc.hive.get.queryOptions({ hiveId: myHive.id }));
  const worlds = useQuery(trpc.world.list.queryOptions());
  const cfg = useQuery(trpc.world.config.queryOptions({ worldId }));
  const world = worlds.data?.find((w) => w.id === worldId);
  const [count, setCount] = useState('');
  const [escortG, setEscortG] = useState('');
  const [escortW, setEscortW] = useState('');
  const send = useMutation(
    trpc.movement.sendGather.mutationOptions({
      onSuccess: () => {
        setCount('');
        setEscortG('');
        setEscortW('');
        void qc.invalidateQueries({ queryKey: trpc.movement.pathKey() });
        void qc.invalidateQueries({ queryKey: trpc.hive.get.queryKey({ hiveId: myHive.id }) });
      },
    }),
  );

  const foragers = hive.data?.units.find((u) => u.type === 'FORAGER')?.count ?? 0;
  const forager = cfg.data?.balance.units.FORAGER;
  const seasonKey = world?.season.season ?? 'TRACHT';
  const speedMult = cfg.data ? Number(cfg.data.seasons[seasonKey].speedMult) : 1;
  const prodMult = cfg.data ? Number(cfg.data.seasons[seasonKey].prodMult) : 1;
  const dist = hexDistance(myHive, field);
  const minutes =
    forager && cfg.data
      ? flightTimeMinutes({
          from: myHive,
          to: field,
          slowestMinPerHex: forager.minPerHex,
          seasonSpeedMult: speedMult,
          worldSpeed: cfg.data.speed,
        })
      : 0;
  const n = Math.max(0, Math.floor(Number(count) || 0));
  const dwell = world?.fieldDwellMinutes ?? 15;
  const carry = forager ? forager.carry * n : 0;
  const estAlone = Math.min(carry, field.baseRate * prodMult * dwell);
  const full = detail.data ? detail.data.occupancies.length >= (world?.fieldSlots ?? 6) : false;
  const quick = [10, 25, 50, 100].filter((x) => x <= foragers);
  const hostileHere = detail.data?.occupancies.some((o) => o.relation === 'HOSTILE') ?? false;
  const alliedHere =
    detail.data?.occupancies.some((o) => o.relation === 'ALLIED' && !o.isMine) ?? false;
  const guards = hive.data?.units.find((u) => u.type === 'GUARD')?.count ?? 0;
  const warriors = hive.data?.units.find((u) => u.type === 'WARRIOR')?.count ?? 0;
  const escortGuards = Math.min(guards, Math.max(0, Math.floor(Number(escortG) || 0)));
  const escortWarriors = Math.min(warriors, Math.max(0, Math.floor(Number(escortW) || 0)));

  return (
    <Card
      title={
        <span>
          {FIELD_ICONS[field.type]} {FIELD_LABELS[field.type]}
        </span>
      }
      action={
        <Badge tone={field.resource === 'nectar' ? 'amber' : 'sky'}>
          {RESOURCE_LABELS[field.resource]}
        </Badge>
      }
    >
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        <dt className="text-stone-400">Position</dt>
        <dd>
          [{field.q}|{field.r}]
        </dd>
        <dt className="text-stone-400">Ertrag</dt>
        <dd>{formatNumber(field.baseRate * prodMult * 60)}/h bei Alleinbesetzung</dd>
        <dt className="text-stone-400">Entfernung</dt>
        <dd>
          {dist} Hex · {formatDuration(minutes * 60)} einfach
        </dd>
        <dt className="text-stone-400">Verweildauer</dt>
        <dd>{dwell} min</dd>
      </dl>

      <h3 className="mt-3 text-sm font-medium">
        Am Feld ({detail.data?.occupancies.length ?? 0}/{world?.fieldSlots ?? 6})
      </h3>
      {detail.data && detail.data.occupancies.length === 0 && (
        <p className="text-xs text-stone-400">Niemand. Der Ertrag gehört dir allein.</p>
      )}
      <ul className="text-xs text-stone-300">
        {detail.data?.occupancies.map((o) => (
          <li key={`${o.playerId}-${o.arrivedAt}`} className="flex justify-between gap-2">
            <span className="flex items-center gap-1">
              {o.allianceTag && <span className="text-stone-500">[{o.allianceTag}]</span>}
              {o.playerName}
              {o.isMine ? (
                <Badge tone="amber">ich</Badge>
              ) : o.relation === 'ALLIED' ? (
                <Badge tone="green">verbündet</Badge>
              ) : o.relation === 'HOSTILE' ? (
                <Badge tone="red">feindlich</Badge>
              ) : null}
            </span>
            <span className="tabular-nums text-stone-400">
              Kraft {formatNumber(o.gatherPower)} · bis {formatAbsRel(o.leavesAt, now)}
            </span>
          </li>
        ))}
      </ul>
      {hostileHere && (
        <p className="mt-2 rounded-md border border-red-900/60 bg-red-950/40 p-2 text-xs text-red-300">
          Feindliche Sammlerinnen am Feld: Bei Ankunft kommt es zum Feldkampf (ohne Wächterwabe).
          Schick Wächterinnen oder Kriegerinnen als Eskorte mit. Die Beziehung wird beim Abflug
          eingefroren.
        </p>
      )}
      {alliedHere && !hostileHere && (
        <p className="mt-2 text-xs text-emerald-400">
          Verbündete am Feld drücken deinen Ertrag nicht.
        </p>
      )}

      <form
        className="mt-3 flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (n > 0)
            send.mutate({
              worldId,
              hiveId: myHive.id,
              fieldId,
              units: { FORAGER: n, GUARD: escortGuards, WARRIOR: escortWarriors },
            });
        }}
      >
        <div className="flex-1">
          <Input
            name="foragers"
            type="number"
            inputMode="numeric"
            label={`${UNIT_LABELS.FORAGER}nen (${formatNumber(foragers)} verfügbar)`}
            min={1}
            max={Math.max(1, foragers)}
            placeholder={`max ${foragers}`}
            value={count}
            onChange={(e) => setCount(e.target.value)}
          />
        </div>
        <Button
          type="submit"
          disabled={n < 1 || n > foragers || send.isPending || full}
          loading={send.isPending}
          title={full ? 'Alle Plätze belegt' : undefined}
        >
          Losschicken
        </Button>
      </form>
      {quick.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {quick.map((x) => (
            <Button key={x} size="sm" variant="ghost" onClick={() => setCount(String(x))}>
              {x}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setCount(String(foragers))}>
            alle
          </Button>
        </div>
      )}
      {(guards > 0 || warriors > 0) && (
        <details className="mt-2 text-xs" open={hostileHere}>
          <summary className="cursor-pointer text-stone-400">
            Eskorte mitschicken
            {escortGuards + escortWarriors > 0 && ` (${escortGuards + escortWarriors})`}
          </summary>
          <div className="mt-1 grid grid-cols-2 gap-2">
            <Input
              name="escortGuards"
              type="number"
              inputMode="numeric"
              label={`${UNIT_LABELS.GUARD}nen (${formatNumber(guards)})`}
              min={0}
              max={guards}
              value={escortG}
              onChange={(e) => setEscortG(e.target.value)}
              disabled={guards === 0}
            />
            <Input
              name="escortWarriors"
              type="number"
              inputMode="numeric"
              label={`${UNIT_LABELS.WARRIOR}nen (${formatNumber(warriors)})`}
              min={0}
              max={warriors}
              value={escortW}
              onChange={(e) => setEscortW(e.target.value)}
              disabled={warriors === 0}
            />
          </div>
        </details>
      )}
      {n > 0 && (
        <p className="mt-2 text-xs text-stone-400">
          Ankunft {formatAbsRel(new Date(now.getTime() + minutes * 60_000), now)} · zurück ≈{' '}
          {formatDuration(minutes * 120 + dwell * 60)} · Ertrag allein ≈ {formatNumber(estAlone)}{' '}
          {RESOURCE_LABELS[field.resource]}
          {carry > 0 && estAlone >= carry && ' (Tragkraft voll)'}
        </p>
      )}
      {send.error && <p className="mt-2 text-sm text-red-400">{send.error.message}</p>}
      {send.isSuccess && (
        <p className="mt-2 text-sm text-emerald-400">Unterwegs. Details unter „Züge".</p>
      )}
    </Card>
  );
}
